import { SpaceApiError, errorFromJsMessage } from './errors'
import type { SpaceTransport } from './transport'
import type {
  ChangeEmailDto,
  ChangePasswordDto,
  ChangePayPassDto,
  EmailAction,
  EmailCodeDelivery,
  ForceChangePasswordDto,
  NotificationSettingsDto,
  PayoutSettingsDto,
  ProfileBaseInfo,
  ProfileBaseInfoResponse,
  SaveContactsDto,
  SecurityConfirmation,
  SecuritySessionResponse,
} from './types'

/** Ручки Space, которые вызывает ядро (пути от корня API). */
export const SPACE_USER_PATHS = {
  baseInfo: '/profile/base-info',
  editContacts: '/profile/edit-contacts',
  editPayments: '/profile/edit-payments',
  uploadAvatar: '/profile/upload/avatar',
  editSettings: '/account/settings/edit-settings',
  initActionSession: '/security/init-action-session',
  editPass: '/security/edit-pass',
  editPayPass: '/security/edit-pay-pass',
  editEmail: '/security/edit-email',
  mfAuthorization: '/profile/settings/mf-authorization',
  deleteAccount: '/profile',
  /** Только с purpose=verify_email: подтверждение текущей почты, сессию не создаёт. */
  emailCode: '/profile/login-by-code',
  emailCodeVerify: '/profile/login-by-code/verify',
} as const

export interface ReadBaseInfoOptions {
  /** Протокол для фото вида `//cdn/...`. В браузере — `location.protocol`. */
  protocol?: string
}

const normalizePhoto = (photo: string | null, protocol: string): string | null => {
  if (!photo) return null
  if (photo.startsWith('//')) return `${protocol}${photo}`
  return photo
}

/**
 * Разбирает ответ base-info: отказ бэкенда — `backend`/`unauthorized`,
 * чужая форма ответа — `bad_response`. Ссылку-приглашение сводит в `reflink`.
 */
export function readBaseInfo(body: unknown, options: ReadBaseInfoOptions = {}): ProfileBaseInfo {
  const response = body as Partial<ProfileBaseInfoResponse> | null
  if (!response || typeof response !== 'object') {
    throw new SpaceApiError('bad_response', 'base-info: response is not an object', { data: body })
  }
  if (response.result !== 'success') {
    if (response.result === 'error') throw errorFromJsMessage(body, 200)
    throw new SpaceApiError('bad_response', 'base-info: unknown result', { data: body })
  }
  const data = response.data
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new SpaceApiError('bad_response', 'base-info: data is missing', { data: body })
  }

  const reflink = data.reflink ?? data.referral_link ?? data.referal_link ?? null
  return {
    ...data,
    photo: normalizePhoto(data.photo, options.protocol ?? 'https:'),
    reflink,
    referral_link: data.referral_link ?? reflink,
    referal_link: data.referal_link ?? reflink,
  }
}

/** Что делать с почтой: привязать (нет или @telegram.org), подтвердить или сменить. */
export function emailAction(profile: Pick<ProfileBaseInfo, 'email' | 'verify_email'> | null | undefined): EmailAction {
  const email = profile?.email?.trim() ?? ''
  if (!email || /@telegram\.org$/i.test(email)) return 'attach'
  return profile?.verify_email === 1 ? 'change' : 'verify'
}

const toForm = (fields: Record<string, unknown>): FormData => {
  const form = new FormData()
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null) form.append(key, String(value))
  }
  return form
}

const withConfirmation = (form: FormData, confirmation?: SecurityConfirmation): FormData => {
  if (confirmation?.confirmAction) form.append('confirmAction', confirmation.confirmAction)
  if (confirmation?.confirmActionCode) form.append('confirmActionCode', confirmation.confirmActionCode)
  return form
}

export type SpaceUserApi = ReturnType<typeof createSpaceUserApi>

/**
 * Методы пользователя Space поверх переданного транспорта. Ответы возвращаются
 * как их отдал транспорт; разбирается только base-info.
 */
export function createSpaceUserApi(transport: SpaceTransport, options: ReadBaseInfoOptions = {}) {
  const post = <T>(path: string, form: FormData) => transport.request<T>({ method: 'POST', path, form })
  const getBaseInfoResponse = () =>
    transport.request<ProfileBaseInfoResponse>({ method: 'GET', path: SPACE_USER_PATHS.baseInfo })

  return {
    getBaseInfoResponse,

    getBaseInfo: async (): Promise<ProfileBaseInfo> => readBaseInfo(await getBaseInfoResponse(), options),

    /** Заводит сессию подтверждения действия и отправляет код (или просит выбрать канал). */
    initActionSession: (actionName: string, payload: Record<string, unknown> = {}) =>
      post<SecuritySessionResponse>(SPACE_USER_PATHS.initActionSession, toForm({ actionName, ...payload })),

    updateContacts: (data: SaveContactsDto) =>
      post<{ result: string }>(SPACE_USER_PATHS.editContacts, toForm({ ...data })),

    changePassword: (data: ChangePasswordDto) =>
      post<{ result: string }>(
        SPACE_USER_PATHS.editPass,
        toForm({ passwordold: data.passwordold, passwordnew: data.passwordnew, passwordretry: data.passwordretry }),
      ),

    /** Смена пароля без старого (MD5-аккаунты): бэкенд требует код SpaceGuard. */
    forceChangePassword: (data: ForceChangePasswordDto) =>
      post<{ result: string }>(
        SPACE_USER_PATHS.editPass,
        toForm({
          passwordnew: data.passwordnew,
          passwordretry: data.passwordretry,
          confirmActionCode: data.confirmActionCode,
          confirmAction: 'force_change_pass',
        }),
      ),

    changePayPassword: (data: ChangePayPassDto) =>
      post<{ result: string }>(
        SPACE_USER_PATHS.editPayPass,
        withConfirmation(
          toForm({ passwordold: data.passwordold, passwordnew: data.passwordnew, passwordretry: data.passwordretry }),
          data,
        ),
      ),

    updatePaymentMethods: (data: PayoutSettingsDto) =>
      post<{ result: string }>(SPACE_USER_PATHS.editPayments, toForm({ ...data })),

    /** `filename` нужен на сервере, где файл приходит как Blob. */
    uploadAvatar: (file: Blob, filename?: string) => {
      const form = new FormData()
      if (filename) form.append('uploadfile', file, filename)
      else form.append('uploadfile', file)
      return post<unknown>(SPACE_USER_PATHS.uploadAvatar, form)
    },

    updateSettings: (data: NotificationSettingsDto) => {
      const form = new FormData()
      if (data.mailing) {
        Object.entries(data.mailing).forEach(([key, value]) => form.append(`mailing[${key}]`, String(value)))
      }
      if (data.company_user_id) form.append('company_user_id', String(data.company_user_id))
      return post<{ result: string }>(SPACE_USER_PATHS.editSettings, form)
    },

    /** Код на новую почту перед сменой (action change_email_new_email). */
    sendNewEmailCode: (email: string) =>
      post<{ result: string }>(
        SPACE_USER_PATHS.initActionSession,
        toForm({ actionName: 'change_email_new_email', email }),
      ),

    changeEmail: (data: ChangeEmailDto) =>
      post<{ result: string }>(
        SPACE_USER_PATHS.editEmail,
        withConfirmation(toForm({ new_email: data.email, code: data.code }), data),
      ),

    /** Код на текущую почту, чтобы её подтвердить. Входа не создаёт. */
    sendCurrentEmailCode: async (email: string): Promise<EmailCodeDelivery> => {
      const response = await transport.request<{ data?: EmailCodeDelivery }>({
        method: 'POST',
        path: SPACE_USER_PATHS.emailCode,
        json: { email, channel: 'email', purpose: 'verify_email' },
      })
      return response?.data ?? {}
    },

    verifyCurrentEmail: async (email: string, code: string): Promise<boolean> => {
      const response = await transport.request<{ data?: { verified?: boolean } }>({
        method: 'POST',
        path: SPACE_USER_PATHS.emailCodeVerify,
        json: { email, code, purpose: 'verify_email' },
      })
      return response?.data?.verified === true
    },

    getMfAuthorization: () =>
      transport.request<{ enabled: boolean; totp_enabled: boolean }>({
        method: 'GET',
        path: SPACE_USER_PATHS.mfAuthorization,
      }),

    /** Выключение требует кода: confirmAction `disable_2fa`. */
    setMfAuthorization: (enabled: boolean, confirmation?: SecurityConfirmation) =>
      post<{ result: string; enabled: boolean }>(
        SPACE_USER_PATHS.mfAuthorization,
        withConfirmation(toForm({ enabled: enabled ? '1' : '0' }), confirmation),
      ),

    /** Первый вызов без кода: бэкенд шлёт письмо с кодом удаления. */
    requestAccountDeletionCode: () =>
      transport.request<{ result: unknown }>({ method: 'DELETE', path: SPACE_USER_PATHS.deleteAccount }),

    deleteAccount: (code: string) =>
      transport.request<{ result: unknown }>({ method: 'DELETE', path: SPACE_USER_PATHS.deleteAccount, json: { code } }),
  }
}
